import React, { useState, useEffect } from 'react';
import { Form, Select, Slider, InputNumber, Card, Row, Col, Typography, Statistic, Progress, message, Divider } from 'antd';
import { SaveOutlined, DollarOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { API } from '@/services/api.service';

const { Text } = Typography;
const { Option } = Select;

export default function ModelSettingsTab({ config }) {
  const { t } = useTranslation(['tables']);
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [isDirty, setIsDirty] = useState(false);

  const pa = (key) => t(`payrollAutomation.agentConfig.${key}`, { ns: 'tables', defaultValue: key });

  // Watch form values for displaying current values
  const temperature = Form.useWatch('temperature', form) ?? 0;
  const maxTokens = Form.useWatch('maxTokens', form) ?? 4096;
  const timeout = Form.useWatch('timeout', form) ?? 120000;

  // Load config into form
  useEffect(() => {
    if (config) {
      form.setFieldsValue({
        modelVersion: config.modelVersion || 'gemini-3.6-flash',
        temperature: config.temperature !== undefined ? config.temperature : 0,
        maxTokens: config.maxTokens || 4096,
        timeout: config.timeout || 120000,
        monthlyCap: config.budget?.monthlyCap || 500,
      });
      setIsDirty(false);
    }
  }, [config, form]);

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => API.agentConfig.updateAgentConfiguration(id, data),
    onSuccess: () => {
      message.success(pa('settingsUpdated'));
      queryClient.invalidateQueries(['agent-config-detail', config._id]);
      queryClient.invalidateQueries(['agent-configs']);
      setIsDirty(false);
    },
    onError: (error) => {
      message.error(error.response?.data?.message || pa('updateFailed'));
    },
  });

  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      updateMutation.mutate({
        id: config._id,
        data: {
          modelVersion: values.modelVersion,
          temperature: values.temperature,
          maxTokens: values.maxTokens,
          timeout: values.timeout,
          budget: {
            monthlyCap: values.monthlyCap,
          },
          changes: 'Model settings updated via UI',
        },
      });
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  if (!config) {
    return <div>{pa('noConfigSelected')}</div>;
  }

  const budgetUsagePercent = config.budgetUsagePercent || 0;
  const budgetColor = budgetUsagePercent >= 80 ? 'red' : budgetUsagePercent >= 60 ? 'orange' : 'green';

  return (
    <div style={{ maxWidth: 900 }}>
      <Form
        form={form}
        layout="vertical"
        onValuesChange={() => setIsDirty(true)}
      >
        {/* Model Selection */}
        <Card title={pa('modelSelection')} style={{ marginBottom: 24 }}>
          <Form.Item
            name="modelVersion"
            label={pa('aiModel')}
            tooltip={pa('modelTooltip')}
            rules={[{ required: true }]}
          >
            <Select size="large">
              {/* Gemini 1.5 and 2.0 were retired by Google (404); a step
                  saved with one runs on Gemini 3.6 Flash. */}
              <Option value="gemini-3.6-flash">
                Gemini 3.6 Flash (Recommended)
              </Option>
              <Option value="claude-sonnet-4-20250514">
                Claude Sonnet 4 (Latest)
              </Option>
              <Option value="claude-3-5-sonnet-20241022">
                Claude 3.5 Sonnet
              </Option>
              <Option value="claude-3-opus-20240229">
                Claude 3 Opus - Highest quality, more expensive
              </Option>
              <Option value="claude-3-haiku-20240307">
                Claude 3 Haiku - Faster and cheaper
              </Option>
            </Select>
          </Form.Item>
        </Card>

        {/* Generation Parameters */}
        <Card title={pa('generationParameters')} style={{ marginBottom: 24 }}>
          {/* Temperature */}
          <div style={{ marginBottom: 32 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <Text strong>{pa('temperature')}</Text>
              <Text type="secondary">{temperature.toFixed(1)}</Text>
            </div>
            <Form.Item
              name="temperature"
              tooltip={pa('temperatureTooltip')}
              style={{ marginBottom: 0 }}
            >
              <Slider
                min={0}
                max={1}
                step={0.1}
                marks={{
                  0: 'Precise',
                  1: 'Creative',
                }}
              />
            </Form.Item>
          </div>

          <Divider style={{ margin: '24px 0' }} />

          {/* Max Tokens */}
          <div style={{ marginBottom: 32 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <Text strong>{pa('maxTokens')}</Text>
              <Text type="secondary">{maxTokens.toLocaleString()}</Text>
            </div>
            <Form.Item
              name="maxTokens"
              tooltip={pa('maxTokensTooltip')}
              style={{ marginBottom: 0 }}
            >
              <Slider
                min={1024}
                max={65536}
                step={1024}
                marks={{
                  1024: '1K',
                  8192: '8K',
                  32768: '32K',
                  65536: '64K',
                }}
              />
            </Form.Item>
          </div>

          <Divider style={{ margin: '24px 0' }} />

          {/* Timeout */}
          <div style={{ marginBottom: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <Text strong>{pa('timeout')}</Text>
              <Text type="secondary">{(timeout / 1000).toFixed(0)}s</Text>
            </div>
            <Form.Item
              name="timeout"
              tooltip={pa('timeoutTooltip')}
              style={{ marginBottom: 0 }}
            >
              <Slider
                min={30000}
                max={300000}
                step={30000}
                marks={{
                  30000: '30s',
                  120000: '2m',
                  300000: '5m',
                }}
              />
            </Form.Item>
          </div>
        </Card>

        {/* Budget Control */}
        <Card title={<><DollarOutlined /> {pa('budgetControl')}</>} style={{ marginBottom: 24 }}>
          <Row gutter={24} style={{ marginBottom: 32 }}>
            <Col span={8}>
              <Statistic
                title={pa('currentMonthSpend')}
                value={config.budget?.currentMonthSpend || 0}
                prefix="$"
                precision={2}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title={pa('monthlyCap')}
                value={config.budget?.monthlyCap || 500}
                prefix="$"
                precision={2}
              />
            </Col>
            <Col span={8}>
              <div style={{ marginBottom: 8 }}>
                <Text strong>{pa('budgetUsage')}</Text>
              </div>
              <Progress
                percent={Math.round(budgetUsagePercent)}
                status={budgetUsagePercent >= 80 ? 'exception' : 'normal'}
                strokeColor={budgetColor}
              />
            </Col>
          </Row>

          <Divider style={{ margin: '24px 0' }} />

          <Form.Item
            label={pa('monthlyCap')}
            name="monthlyCap"
            tooltip={pa('monthlyCapTooltip')}
            style={{ marginBottom: 8 }}
          >
            <InputNumber
              min={100}
              max={5000}
              step={100}
              prefix="$"
              size="large"
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Text type="secondary" style={{ fontSize: 12 }}>
            {pa('budgetResetInfo')}
          </Text>
        </Card>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            className="btn-primary"
            onClick={handleSave}
            disabled={!isDirty || updateMutation.isPending}
          >
            <SaveOutlined style={{ marginRight: 4 }} />
            {updateMutation.isPending ? t('loading', { ns: 'tables' }) : pa('saveSettings')}
          </button>
        </div>
      </Form>
    </div>
  );
}
